import React from "react";
import { PomTestData } from "../../typings/pom";
import { 
  Sparkles, 
  Target, 
  Lightbulb, 
  MessageSquare, 
  AlertCircle, 
  Briefcase, 
  Calendar,
  Info
} from "lucide-react";
import { formatBold } from "../../utils/vak";

interface PomAiRecommendationProps {
  testData: PomTestData;
}

const PomAiRecommendation: React.FC<PomAiRecommendationProps> = ({ testData }) => {
  if (!testData.ai_generated || !testData.ai_summary) {
    return null;
  }

  const parseJsonArray = (data: string | string[] | undefined): string[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    try {
      return JSON.parse(data);
    } catch (e) {
      console.error("Failed to parse AI data:", e);
      return [];
    }
  };

  const sections = [
    {
      title: "Kekuatan Personal",
      icon: <Target className="text-blue-500 w-5 h-5" />,
      content: parseJsonArray(testData.ai_strengths),
      bgColor: "bg-blue-50"
    },
    {
      title: "Rekomendasi Gaya Belajar",
      icon: <Lightbulb className="text-amber-500 w-5 h-5" />,
      content: parseJsonArray(testData.ai_learning_style),
      bgColor: "bg-amber-50"
    },
    {
      title: "Komunikasi & Gaya Sosial",
      icon: <MessageSquare className="text-green-500 w-5 h-5" />,
      content: parseJsonArray(testData.ai_communication),
      bgColor: "bg-green-50"
    },
    {
      title: "Tantangan & Area Pertumbuhan",
      icon: <AlertCircle className="text-red-500 w-5 h-5" />,
      content: parseJsonArray(testData.ai_challenges),
      bgColor: "bg-red-50"
    },
    {
      title: "Eksplorasi Karir & Studi",
      icon: <Briefcase className="text-purple-500 w-5 h-5" />,
      content: parseJsonArray(testData.ai_career),
      bgColor: "bg-purple-50"
    },
    {
      title: "Rencana Aksi (3-6 Bulan)",
      icon: <Calendar className="text-indigo-500 w-5 h-5" />,
      content: parseJsonArray(testData.ai_action_plan),
      bgColor: "bg-indigo-50"
    }
  ];

  return (
    <div className="mt-8 space-y-6">
      <div className="rounded-xl shadow-lg overflow-hidden">
        <div className="gradient-accent px-6 py-4 flex items-center gap-3">
          <Sparkles className="text-white w-6 h-6 animate-pulse" />
          <div>
            <h3 className="text-white font-bold text-xl">
              Profil Orientasi Bakat (TOP)
            </h3>
            <p className="text-blue-100 text-sm">
              Analisis cerdas berdasarkan kecenderungan alami Anda
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-white/10">
          <div className="flex items-start gap-3">
            <Info className="text-foreground w-5 h-5 mt-1 flex-shrink-0" />
            <p
              className="text-foreground text-sm leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: formatBold(testData.ai_summary),
              }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sections.map((section, idx) => (
          <div
            key={idx}
            className={`${section.bgColor} rounded-xl border border-gray-100 p-5 shadow-sm transition-all hover:shadow-md`}
          >
            <div className="flex items-center gap-2 mb-4">
              {section.icon}
              <h4 className="font-bold text-gray-800">{section.title}</h4>
            </div>
            <ul className="space-y-2">
              {section.content.map((item, i) => (
                <li key={i} className="flex gap-2 text-sm text-gray-700">
                  <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                  <span
                    dangerouslySetInnerHTML={{ __html: formatBold(item) }}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="bg-gray-50 rounded-lg p-4 text-center border border-dashed border-gray-300">
        <p className="text-xs text-gray-500 italic">
          * Rekomendasi ini dihasilkan oleh AI Educational Psychologist untuk
          membantu eksplorasi potensi diri. Keputusan akhir tetap berada di
          tangan Anda dengan bimbingan orang tua dan guru.
        </p>
      </div>
    </div>
  );
};

export default PomAiRecommendation;
